import { useEffect, useState } from "react";
import { Plus, Trash2, ShoppingCart, Crown, X, Truck, CheckCircle2, Package, FileDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatFCFA, formatDate } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const invoiceStatusLabel = (s) => {
  const map = {
    "to invoice": "À facturer",
    invoiced: "Facturé",
    no: "Rien à facturer",
    upselling: "Vente add.",
  };
  return map[s] || "Confirmée";
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState("");
  const [pendingGifts, setPendingGifts] = useState([]);
  const [lines, setLines] = useState([{ product_id: "", quantity: 1 }]);
  const [validating, setValidating] = useState(null);
  const [statusFilter, setStatusFilter] = useState("Toutes");

  const load = () => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(load, []);

  useEffect(() => {
    if (clientId) {
      api.get(`/clients/${clientId}/pending-gifts`).then((r) => setPendingGifts(r.data)).catch(() => setPendingGifts([]));
    } else {
      setPendingGifts([]);
    }
  }, [clientId]);

  const selectedClient = clients.find((c) => c.id === clientId);
  const passActive = selectedClient && selectedClient.pass_actif && !selectedClient.pass_expired;

  const addLine = () => setLines([...lines, { product_id: "", quantity: 1 }]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));
  const updateLine = (i, patch) => setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const giftDiscount = pendingGifts
    .filter((g) => g.type === "discount")
    .reduce((s, g) => s + (g.value || 0), 0);

  const preview = () => {
    let subtotal = 0;
    lines.forEach((l) => {
      const p = products.find((x) => x.id === l.product_id);
      if (p) subtotal += p.price * (parseInt(l.quantity, 10) || 0);
    });
    const afterPass = passActive ? Math.round(subtotal * 0.85) : subtotal;
    const total = Math.max(0, afterPass - giftDiscount);
    return { subtotal, afterPass, total, discount: subtotal - afterPass };
  };

  const openNew = () => {
    setClientId("");
    setLines([{ product_id: "", quantity: 1 }]);
    setOpen(true);
  };

  const save = async () => {
    if (!clientId) { toast.error("Sélectionnez un client"); return; }
    const items = lines.filter((l) => l.product_id && l.quantity > 0).map((l) => ({ product_id: l.product_id, quantity: parseInt(l.quantity, 10) }));
    if (items.length === 0) { toast.error("Ajoutez au moins un produit"); return; }
    try {
      await api.post("/orders", { client_id: clientId, items });
      toast.success(passActive ? "Commande créée avec -15% Privilège" : "Commande créée");
      setOpen(false); load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur lors de la création");
    }
  };

  const remove = async (id) => { await api.delete(`/orders/${id}`); toast.success("Commande supprimée"); load(); };

  const prepareOrder = async (o) => {
    setValidating(o.id);
    try {
      await api.post(`/orders/${o.id}/prepare`);
      toast.success(`Commande de ${o.client_name} en préparation`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur");
    } finally {
      setValidating(null);
    }
  };

  const downloadInvoice = async (o) => {
    try {
      const res = await api.get(`/orders/${o.id}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `facture_${o.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Échec du téléchargement de la facture");
    }
  };

  const validateDelivery = async (o) => {
    setValidating(o.id);
    try {
      const r = await api.post(`/orders/${o.id}/validate-delivery`);
      const pts = r.data.points_credited || 0;
      const odoo = r.data.odoo || {};
      const wa = r.data.whatsapp || {};
      toast.success(`Livraison validée — ${pts} NaturaPoints crédités à ${o.client_name}`);
      if (wa.sent) {
        toast.success(`Notification WhatsApp envoyée à ${o.client_name}`);
      } else if (wa.configured) {
        toast.error(`WhatsApp non envoyé : ${wa.error || "erreur inconnue"}`);
      }
      if (odoo.sent) {
        toast.success(`Envoyée à Odoo (${odoo.odoo_order_name || "commande créée"})`);
      } else {
        toast.error(`Odoo non synchronisé : ${odoo.error || "erreur inconnue"}`);
      }
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de la validation");
    } finally {
      setValidating(null);
    }
  };

  const pv = preview();
  const statusTabs = ["Toutes", "En attente", "En préparation", "Livrée"];
  const visibleOrders = statusFilter === "Toutes" ? orders : orders.filter((o) => (o.status || "En attente") === statusFilter);
  const countBy = (s) => (s === "Toutes" ? orders.length : orders.filter((o) => (o.status || "En attente") === s).length);
  const stockIssues = lines
    .map((l) => {
      const p = products.find((x) => x.id === l.product_id);
      const qty = parseInt(l.quantity, 10) || 0;
      return p && qty > p.stock ? { name: p.name, stock: p.stock } : null;
    })
    .filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-in-up" data-testid="orders-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Ventes</p>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Commandes</h1>
        </div>
        <button data-testid="add-order-button" onClick={openNew} className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#0ea5e9] hover:bg-[#0284c7] text-white font-semibold text-sm transition-colors duration-200">
          <Plus className="h-4 w-4" /> Nouvelle commande
        </button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="order-status-filter">
        {statusTabs.map((s) => (
          <button
            key={s}
            data-testid={`filter-${s.replace(/\s/g, "-").toLowerCase()}`}
            onClick={() => setStatusFilter(s)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold transition-colors duration-200 ${statusFilter === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          >
            {s} <span className="opacity-60">({countBy(s)})</span>
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {visibleOrders.length === 0 && (
          <div className="bg-white rounded-lg border border-slate-200 px-6 py-12 text-center text-slate-400">
            <ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-40" />
            Aucune commande pour l'instant.
          </div>
        )}
        {visibleOrders.map((o) => (
          <div key={o.id} data-testid={`order-card-${o.id}`} className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-lg font-semibold text-slate-900">{o.client_name}</h3>
                  {o.pass_applied && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                      <Crown className="h-3 w-3" /> -15%
                    </span>
                  )}
                  {o.points_earned > 0 && (
                    <span data-testid={`order-points-${o.id}`} className="text-xs font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
                      +{o.points_earned} pts
                    </span>
                  )}
                  {o.gifts_applied && o.gifts_applied.length > 0 && (
                    <span data-testid={`order-gift-${o.id}`} className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      🎁 Cadeau appliqué
                    </span>
                  )}
                  {o.status === "Livrée" ? (
                    <span data-testid={`order-status-${o.id}`} className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 inline-flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Livrée
                    </span>
                  ) : o.status === "En préparation" ? (
                    <span data-testid={`order-status-${o.id}`} className="text-xs font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 inline-flex items-center gap-1">
                      <Package className="h-3 w-3" /> En préparation
                    </span>
                  ) : (
                    <span data-testid={`order-status-${o.id}`} className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                      En attente
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 tabular">{formatDate(o.date)}</p>
              </div>
              <button data-testid={`delete-order-${o.id}`} onClick={() => remove(o.id)} className="p-2 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors duration-150">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="border-t border-slate-100 pt-3 space-y-1.5">
              {o.items.map((it, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{it.product_name} × {it.quantity}</span>
                  <span className="tabular text-slate-700">{formatFCFA(it.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 mt-3 pt-3 flex items-center justify-between">
              <div className="flex flex-col gap-0.5">
                {o.pass_applied && (
                  <span className="text-xs text-amber-600">Économie membre : <span className="tabular font-semibold">{formatFCFA(o.discount_amount)}</span></span>
                )}
                {o.gift_discount > 0 && (
                  <span className="text-xs text-emerald-600">Cadeau remise : <span className="tabular font-semibold">- {formatFCFA(o.gift_discount)}</span></span>
                )}
              </div>
              <span className="font-display text-lg font-bold text-slate-900 tabular">{formatFCFA(o.total)}</span>
            </div>
            {o.status !== "Livrée" && (
              <div className="mt-4 space-y-2">
                {o.status === "En attente" && (
                  <button
                    data-testid={`prepare-order-${o.id}`}
                    onClick={() => prepareOrder(o)}
                    disabled={validating === o.id}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 font-semibold text-sm transition-colors duration-200 disabled:opacity-60"
                  >
                    <Package className="h-4 w-4" /> {validating === o.id ? "…" : "Mettre en préparation (optionnel)"}
                  </button>
                )}
                <button
                  data-testid={`validate-delivery-${o.id}`}
                  onClick={() => validateDelivery(o)}
                  disabled={validating === o.id}
                  className="w-full flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-base uppercase tracking-wider shadow-lg shadow-blue-600/30 transition-colors duration-200 disabled:opacity-60"
                >
                  <Truck className="h-5 w-5" /> {validating === o.id ? "Validation en cours…" : "Valider la livraison"}
                </button>
              </div>
            )}
            {o.status === "Livrée" && (
              <div className="mt-4 space-y-2">
                <div className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-green-50 text-green-700 font-semibold text-sm">
                  <CheckCircle2 className="h-4 w-4" /> Livrée · +{o.points_earned || 0} pts crédités
                </div>
                {o.odoo_sent && o.odoo_order_name && (
                  <div data-testid={`odoo-status-${o.id}`} className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg bg-indigo-50 text-indigo-700 text-sm">
                    <span className="inline-flex items-center gap-2 font-semibold">
                      {o.odoo_order_url ? (
                        <a href={o.odoo_order_url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted hover:decoration-solid inline-flex items-center gap-1">
                          Odoo {o.odoo_order_name} <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <>Odoo {o.odoo_order_name}</>
                      )}
                    </span>
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white">{invoiceStatusLabel(o.odoo_invoice_status)}</span>
                  </div>
                )}
                <button
                  data-testid={`invoice-${o.id}`}
                  onClick={() => downloadInvoice(o)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-sm transition-colors duration-200"
                >
                  <FileDown className="h-4 w-4" /> Télécharger la facture (PDF)
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="order-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Nouvelle commande</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Client</label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="order-client-select"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.pass_actif && !c.pass_expired ? "👑" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {passActive && (
                <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                  <Crown className="h-3 w-3" /> Membre Privilège — remise -15% appliquée automatiquement
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Produits</label>
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex-1">
                      <Select value={l.product_id} onValueChange={(v) => updateLine(i, { product_id: v })}>
                        <SelectTrigger data-testid={`order-product-select-${i}`}><SelectValue placeholder="Produit" /></SelectTrigger>
                        <SelectContent>
                          {products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>{p.name} — {formatFCFA(p.price)} · stock {p.stock}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <input data-testid={`order-qty-${i}`} type="number" min="1" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} className="w-16 px-2 py-2 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-500 text-center" />
                    {lines.length > 1 && (
                      <button onClick={() => removeLine(i)} className="p-2 text-slate-400 hover:text-red-600 transition-colors duration-150"><X className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
              </div>
              <button data-testid="add-line-button" onClick={addLine} className="mt-2 text-sm text-sky-600 font-medium hover:text-sky-700 transition-colors duration-150">+ Ajouter un produit</button>
            </div>

            <div className="rounded-lg bg-slate-50 p-4 space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Sous-total</span><span className="tabular">{formatFCFA(pv.subtotal)}</span>
              </div>
              {passActive && (
                <div className="flex justify-between text-amber-600">
                  <span>Remise Privilège (-15%)</span><span className="tabular">- {formatFCFA(pv.discount)}</span>
                </div>
              )}
              {pendingGifts.map((g) => (
                <div key={g.id} data-testid={`gift-line-${g.id}`} className="flex justify-between text-emerald-600">
                  <span>🎁 {g.reward_name}</span>
                  <span className="tabular">{g.type === "discount" ? `- ${formatFCFA(g.value)}` : "Offert"}</span>
                </div>
              ))}
              <div className="flex justify-between font-bold text-slate-900 pt-1.5 border-t border-slate-200">
                <span>Total</span><span className="tabular" data-testid="order-total-preview">{formatFCFA(pv.total)}</span>
              </div>
            </div>
            {stockIssues.length > 0 && (
              <div data-testid="stock-warning" className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                Stock insuffisant : {stockIssues.map((s) => `${s.name} (${s.stock} dispo)`).join(", ")}
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium transition-colors duration-200">Annuler</button>
            <button data-testid="save-order-button" onClick={save} disabled={stockIssues.length > 0} className="px-4 py-2 rounded-lg bg-[#0ea5e9] hover:bg-[#0284c7] text-white text-sm font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">Créer la commande</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
